import { useContext, useMemo, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import React from 'react';
import { Popper, Grow, Paper, ClickAwayListener, MenuList, MenuItem } from '@mui/material';
import UserContext from '../contexts/UserContext';
import AuthContext from '../contexts/AuthContext';
import SideHeaderButton from './SideHeaderButton';
import SideHeaderLink from './SideHeaderLink';
import { useTranslation } from 'react-i18next';
import LogoutIcon from '@mui/icons-material/Logout';
import { headerRoutes, newRestaurantHeaderRoute } from '../config/pageRoutes';
import SideHeaderLangSelector from './SideHeaderLangSelector';
import { hasRole } from '../models/User';
import { RoleType } from '../models/Role';


export default function SideHeader() {
    const { t, i18n } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const { logout } = useContext(AuthContext);
    const { restaurantSettings, selectedRestaurant, user, setSelectedRestaurantShortName } = useContext(UserContext);
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    const displayedRestaurants = useMemo(() => {
        return restaurantSettings.filter(resto => resto.restaurantShortName !== selectedRestaurant?.restaurantShortName);
    }, [restaurantSettings, selectedRestaurant]);
    const displayedPages = useMemo(() => {
        const relevantRoles = user.roles
            .filter(role => role.restaurantSetting != null && role.restaurantSetting.restaurantShortName === selectedRestaurant?.restaurantShortName)
            .map(role => role.type);

        const routes = headerRoutes.filter(route => {
            if (relevantRoles.some(role => route.roles.includes(role))) {
                return true;
            }
            return false;
        });
        if (!hasRole(user, RoleType.OWNER)) {
            routes.push(newRestaurantHeaderRoute)
        }
        return routes;
    }, [selectedRestaurant]);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }
        setOpen(false);
    };

    function handleListKeyDown(event: React.KeyboardEvent) {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    }

    function handleOptionChange(shortName: string) {
        setSelectedRestaurantShortName(shortName);
        setOpen(false);
    }

    function expandContainerClassNames() {
        if (isExpanded) {
            return "flex gap-1";
        }
        return "text-center ";
    }

    function buttonClassName() {
        if (restaurantSettings.length > 1) {
            return "hover:bg-primary-dark";
        }
        return "";
    }

    return (
        <div
            className={`bg-primary-dark text-white h-screen flex flex-col items-start ${isExpanded ? 'w-64' : 'w-[70px]'} transition-all duration-300 ease-in-out`}>
            {/* Toggle Button */}
            <div className="bg-primary pt-[18px] pb-[10px] rounded-b-[15px] px-2 w-full">
                <div className={`${expandContainerClassNames()}`}>
                    <div className="grow">
                        {isExpanded && (
                            <button
                                ref={anchorRef}
                                onClick={handleToggle}
                                className={`${buttonClassName()} text-white font-bold h-[44px] px-2  transition-all rounded-[8px]`}
                                disabled={restaurantSettings.length <= 1}
                            >
                                <div className="flex">
                                    <div>
                                        {selectedRestaurant?.name}
                                    </div>
                                </div>
                            </button>
                        )}
                    </div>
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="bg-transparent rounded-[10px] hover:bg-primary-dark p-1 transition-all"
                    >
                        {isExpanded && (
                            <ArrowBackIcon
                                className="text-white"
                                style={{ fontSize: "36px" }}
                            />
                        )}
                        {!isExpanded && (
                            <MenuIcon
                                className="text-white"
                                style={{ fontSize: "36px" }}
                            />
                        )}
                    </button>
                </div>
            </div>
            {/* Menu Items */}
            <nav className="flex flex-col gap-[10px] px-2 mt-2 w-full">
                {displayedPages.map(route => {
                    return <SideHeaderLink
                        key={route.title}
                        icon={route.icon}
                        label={route.title}
                        path={route.mainRoute.path.replace(":lang", i18n.language)}
                        isExpanded={isExpanded}
                        title={route.title}
                        isPremiumFeature={route.isPremiumFeature}
                    />
                })}
                <SideHeaderButton
                    icon={<LogoutIcon />}
                    label={t('navigation.logout')}
                    isExpanded={isExpanded}
                    onClick={() => logout()}
                />
            </nav>
            <div className="flex flex-col justify-end grow px-2 pb-1 w-full">
                <SideHeaderLangSelector
                    isExpanded={isExpanded}
                />
            </div>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="top-start"
                transition
                disablePortal
                style={{ zIndex: 1000, textAlign: "center", width: `300px` }}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === 'bottom-start' ? 'left top' : 'left bottom',
                            marginLeft: "10px",
                            marginRight: "10px",
                        }}
                    >
                        <Paper
                            style={{ maxHeight: "360px", overflowY: "auto", }}
                        >
                            <ClickAwayListener onClickAway={handleClose}>
                                <div
                                    className=""
                                >
                                    {displayedRestaurants.length === 0 && (
                                        <div className="text-utility-mutedIcon text-[16px] pt-2">
                                            {t("common.no_restaurant")}
                                        </div>
                                    )}
                                    <MenuList
                                        autoFocusItem={false}
                                        onKeyDown={handleListKeyDown}
                                    >
                                        {displayedRestaurants.map(option => {
                                            return <MenuItem
                                                key={option.restaurantShortName}
                                                onClick={() => handleOptionChange(option.restaurantShortName)}

                                            >
                                                {option.name}
                                            </MenuItem>
                                        })}
                                    </MenuList>
                                </div>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </div>
    );
}